import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Form, FormActions, Input, Modal, PhoneInput, Row, SelectInput, Title } from 'ui';
import { countries, states } from 'common/utils';

import type { DeliveryData, Option } from '../../types';

export type DeliveryFormData = DeliveryData;

type P = {
  initialData?: DeliveryFormData;
  onBack: () => void;
  onSubmit: (values: DeliveryFormData) => void;
  user: {
    apt?: string;
    city?: string;
    country?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    state?: string;
    street?: string;
    zip?: string;
  };
};

const DeliveryInfo = ({ initialData, onBack, onSubmit, user }: P) => {
  const { t } = useTranslation();
  const { t: translationCountry } = useTranslation('translationCountries');
  const { t: translationState } = useTranslation('translationStates');
  const [form] = Form.useForm<DeliveryFormData>();

  const initialValues: Partial<DeliveryFormData> = initialData ?? {
    address1: user.street,
    address2: user.apt,
    city: user.city,
    country: user.country,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    phone: user.phone,
    state: user.state,
    zip: user.zip,
  };

  const [isStateEnabled, setIsStateEnabled] = useState(initialValues.country === 'US');

  const countryOptions: Option[] = useMemo(
    () => countries.map((country) => ({ label: translationCountry(country.translationKey), value: country.code })),
    [translationCountry],
  );

  const stateOptions: Option[] = useMemo(
    () => states.map((state) => ({ label: translationState(state.translationKey), value: state.code })),
    [translationState],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
  const handleValuesChange = (_: any, values: DeliveryFormData) => {
    setIsStateEnabled(values.country === 'US');
  };

  const handleFinish = (values: DeliveryFormData) => {
    onSubmit(values);
  };

  return (
    <Form<DeliveryFormData>
      form={form}
      initialValues={initialValues}
      onFinish={handleFinish}
      onValuesChange={handleValuesChange}
    >
      <fieldset>
        <legend>
          <Title level={4} marginBottom>
            Delivery information
          </Title>
        </legend>
        <Row gutter={24}>
          <Col md={{ span: 12 }} xs={{ span: 24 }}>
            <Form.Item<DeliveryFormData>
              label="First name"
              name="firstName"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input autoComplete="given-name" />
            </Form.Item>
          </Col>
          <Col md={{ span: 12 }} xs={{ span: 24 }}>
            <Form.Item<DeliveryFormData>
              label="Last name"
              name="lastName"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input autoComplete="family-name" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item<DeliveryFormData>
              label="E-mail"
              name="email"
              rules={[
                {
                  type: 'email',
                },
                {
                  required: true,
                },
              ]}
            >
              <Input autoComplete="email" />
            </Form.Item>
          </Col>
          <Col span={24}>
            <PhoneInput
              input={{
                autoComplete: 'tel',
              }}
              item={{
                label: 'Phone number',
                name: 'phone',
                rules: [
                  {
                    message: t(
                      'Error: Phone number is required. Please enter a valid phone number (e.g., 201 555 0123)',
                    ),
                    required: true,
                  },
                ],
              }}
            />
          </Col>
          <Col span={24}>
            <SelectInput
              input={{
                getOptionLabel: (option) => option.label,
                getOptionValue: (option) => option.value,
                options: countryOptions,
                placeholder: t('Country'),
                showSearch: { optionFilterProp: 'label' },
              }}
              item={{
                'data-test-id': 'select-country',
                label: 'Country',
                name: 'country',
                rules: [
                  {
                    required: true,
                  },
                ],
              }}
            />
          </Col>
          {isStateEnabled ? (
            <Col span={24}>
              <SelectInput
                input={{
                  getOptionLabel: (option) => option.label,
                  getOptionValue: (option) => option.value,
                  options: stateOptions,
                  showSearch: true,
                }}
                item={{
                  'data-test-id': 'select-state',
                  label: 'State',
                  name: 'state',
                  rules: [
                    {
                      required: true,
                    },
                  ],
                }}
              />
            </Col>
          ) : null}
          <Col md={{ span: 12 }} xs={{ span: 24 }}>
            <Form.Item<DeliveryFormData>
              label="City"
              name="city"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input autoComplete="address-level2" />
            </Form.Item>
          </Col>
          <Col md={{ span: 12 }} xs={{ span: 24 }}>
            <Form.Item<DeliveryFormData>
              label="ZIP code"
              name="zip"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input autoComplete="postal-code" />
            </Form.Item>
          </Col>
          <Col md={{ span: 12 }} xs={{ span: 24 }}>
            <Form.Item<DeliveryFormData>
              label="Street"
              name="address1"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input autoComplete="address-line1" />
            </Form.Item>
          </Col>
          <Col md={{ span: 12 }} xs={{ span: 24 }}>
            <Form.Item<DeliveryFormData> label="Apartment" name="address2">
              <Input autoComplete="address-line2" />
            </Form.Item>
          </Col>
        </Row>
      </fieldset>
      <Modal.HugContent.Footer>
        <FormActions right={{ onClick: onBack }} submit={{ children: 'Continue' }} />
      </Modal.HugContent.Footer>
    </Form>
  );
};

export default DeliveryInfo;
